/* eslint-disable no-console */
import React, { useEffect, useState, useCallback } from 'react';
import AdminLayout from './AdminLayout.component';
import {
  getAllSourceTexts,
  getLanguageTranslations,
  createTranslation,
  updateTranslation,
} from '../api/admin-translations.api';
import { getLocales } from '@/modules/core/client/utils/locales';
import {
  go,
  getRouteParams,
  $on,
  getUser,
} from '@/modules/core/client/services/angular-compat';
import * as usersApi from '@/modules/users/client/api/users.api';
import i18n from '@/config/client/i18n';

export default function AdminTranslateEdit() {
  const getLanguageFromUrl = useCallback(() => {
    let langCode = null;

    // Try to get from Angular route params first
    try {
      const params = getRouteParams();
      if (params && params.language) {
        langCode = params.language;
      }
    } catch (err) {
      console.log('Failed to get Angular route params:', err);
    }

    // Check URL hash for Angular UI Router
    if (!langCode) {
      const hash = window.location.hash;
      if (hash) {
        const hashMatch = hash.match(/\/admin\/translate\/show\/([^/]+)/);
        if (hashMatch && hashMatch[1]) {
          langCode = decodeURIComponent(hashMatch[1]);
        }
      }
    }

    // Check URL path
    if (!langCode) {
      const path = window.location.pathname;
      const match = path.match(/\/admin\/translate\/show\/([^/]+)/);
      if (match && match[1]) {
        langCode = decodeURIComponent(match[1]);
      }
    }

    // Check query parameters
    if (!langCode) {
      const urlParams = new URLSearchParams(window.location.search);
      const langParam = urlParams.get('language');
      if (langParam) {
        langCode = decodeURIComponent(langParam);
      }
    }

    // Normalize language code (handle both pt-BR and pt_BR formats)
    if (langCode) {
      langCode = langCode.trim().replace(/-/g, '_');
    }

    // Default to English if nothing found
    return langCode || 'en';
  }, []);

  const [language, setLanguage] = useState(() => getLanguageFromUrl());
  const [sourceTexts, setSourceTexts] = useState([]);
  const [translations, setTranslations] = useState([]);

  // Initialize i18n language on mount - check both URL and cookie
  useEffect(() => {
    const urlLanguage = getLanguageFromUrl();

    // Check cookie for saved language preference
    const cookieMatch = document.cookie.match(/i18n=([^;]+)/);
    const cookieLanguage = cookieMatch ? cookieMatch[1] : null;

    // Check if user has locale saved in database
    const user = getUser();
    const dbLocale = user?.locale?.trim() || null;

    // Priority: URL language > Database locale > Cookie > current i18n language
    const targetLanguage =
      urlLanguage || dbLocale || cookieLanguage || i18n.language;

    // If URL has a language, ALWAYS save it to cookie so it persists when navigating away
    // This ensures the language selection persists across the entire app
    if (urlLanguage) {
      const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
      document.cookie = `i18n=${urlLanguage}; path=/; max-age=${maxAge}; SameSite=Lax`;
      console.log(
        '[AdminTranslateEdit init] URL language detected, saved to cookie:',
        urlLanguage,
      );
    }

    if (targetLanguage && targetLanguage !== i18n.language) {
      console.log(
        'Initializing i18n language to:',
        targetLanguage,
        '(from URL:',
        urlLanguage,
        ', cookie:',
        cookieLanguage,
        ')',
      );
      i18n.changeLanguage(targetLanguage).then(() => {
        // Save to cookie to ensure persistence across entire app navigation
        // This is critical so language persists when admin navigates away from translate page
        if (i18n.services?.languageDetector?.cacheUserLanguage) {
          i18n.services.languageDetector.cacheUserLanguage(targetLanguage);
        }

        // Manual cookie fallback to ensure it's saved globally
        const cookieName = 'i18n';
        const cookieValue = targetLanguage;
        const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
        const cookieString = `${cookieName}=${cookieValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
        document.cookie = cookieString;
        console.log(
          '[AdminTranslateEdit init] Saved language to cookie:',
          cookieString,
        );
        console.log(
          '[AdminTranslateEdit init] Verified cookie:',
          document.cookie.match(/i18n=([^;]+)/),
        );

        // Reload all namespaces for the initial language
        const namespaces = i18n.options.ns || ['translation'];
        if (Array.isArray(namespaces)) {
          Promise.all(
            namespaces.map(ns => i18n.reloadResources(targetLanguage, ns)),
          ).catch(err => {
            console.warn('Failed to reload namespaces on init:', err);
          });
        } else {
          i18n.reloadResources(targetLanguage, namespaces).catch(err => {
            console.warn('Failed to reload namespace on init:', err);
          });
        }
      });

      // If URL language is different from state, update state
      if (urlLanguage && urlLanguage !== language) {
        setLanguage(urlLanguage);
      } else if (
        cookieLanguage &&
        cookieLanguage !== language &&
        !urlLanguage
      ) {
        // If we're using cookie language and it's different from state, update state
        setLanguage(cookieLanguage);
      }
    } else if (
      cookieLanguage &&
      cookieLanguage !== i18n.language &&
      !urlLanguage
    ) {
      // If cookie exists but i18n doesn't match, update i18n and ensure cookie is saved
      console.log('Syncing i18n to cookie language:', cookieLanguage);
      i18n.changeLanguage(cookieLanguage).then(() => {
        // Ensure cookie is saved
        const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
        document.cookie = `i18n=${cookieLanguage}; path=/; max-age=${maxAge}; SameSite=Lax`;
        console.log(
          '[AdminTranslateEdit] Synced and saved cookie:',
          cookieLanguage,
        );
      });
    } else if (urlLanguage) {
      // If URL has language, ensure it's saved to cookie for persistence across app
      // This is critical so language persists when admin navigates away from translate page
      const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
      document.cookie = `i18n=${urlLanguage}; path=/; max-age=${maxAge}; SameSite=Lax`;
      console.log(
        '[AdminTranslateEdit] URL language detected, saved to cookie for global persistence:',
        urlLanguage,
      );
    }
    // Run only once on mount - getLanguageFromUrl is stable and doesn't need to be in deps
  }, []);
  const [selectedSourceText, setSelectedSourceText] = useState(null);
  const [translationValue, setTranslationValue] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const locales = getLocales();
  // Normalize language for comparison (handle both pt-BR and pt_BR formats)
  const normalizedLanguage = language?.replace(/-/g, '_') || 'en';
  const currentLanguage = locales.find(
    l =>
      l.code === normalizedLanguage ||
      l.code.replace(/-/g, '_') === normalizedLanguage,
  );

  // Listen for Angular route changes
  useEffect(() => {
    const handleRouteChange = async () => {
      const newLanguage = getLanguageFromUrl();
      if (newLanguage && newLanguage !== language) {
        console.log('Language changed to:', newLanguage);

        // Change the i18n language so UI updates
        // This also saves to cookie automatically via LanguageDetector
        await i18n.changeLanguage(newLanguage);

        // Explicitly save to cookie to ensure persistence across entire app navigation
        // This is critical so language persists when admin navigates away from translate page
        if (i18n.services?.languageDetector?.cacheUserLanguage) {
          i18n.services.languageDetector.cacheUserLanguage(newLanguage);
        }

        // Manual cookie fallback to ensure language persists globally
        const cookieName = 'i18n';
        const cookieValue = newLanguage;
        const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
        const cookieString = `${cookieName}=${cookieValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
        document.cookie = cookieString;
        console.log(
          '[AdminTranslateEdit route change] Saved language to cookie:',
          cookieString,
        );
        console.log(
          '[AdminTranslateEdit route change] Verified cookie:',
          document.cookie.match(/i18n=([^;]+)/),
        );

        // Reload all namespaces to ensure all translations are loaded
        const namespaces = i18n.options.ns || ['translation'];
        if (Array.isArray(namespaces)) {
          await Promise.all(
            namespaces.map(ns =>
              i18n.reloadResources(newLanguage, ns).catch(err => {
                console.warn(`Failed to reload namespace ${ns}:`, err);
              }),
            ),
          );
        } else {
          await i18n.reloadResources(newLanguage, namespaces).catch(err => {
            console.warn(`Failed to reload namespace ${namespaces}:`, err);
          });
        }

        // Reset selected source text when language changes
        setSelectedSourceText(null);
        setTranslationValue('');
        setLanguage(newLanguage);
      }
    };

    // Check language on mount and if it differs from state
    const currentLanguageFromUrl = getLanguageFromUrl();
    if (currentLanguageFromUrl && currentLanguageFromUrl !== language) {
      // Change i18n language on mount if different
      i18n.changeLanguage(currentLanguageFromUrl).then(() => {
        const namespaces = i18n.options.ns || ['translation'];
        if (Array.isArray(namespaces)) {
          Promise.all(
            namespaces.map(ns =>
              i18n.reloadResources(currentLanguageFromUrl, ns),
            ),
          );
        } else {
          i18n.reloadResources(currentLanguageFromUrl, namespaces);
        }
      });

      setSelectedSourceText(null);
      setTranslationValue('');
      setLanguage(currentLanguageFromUrl);
    }

    // Listen for Angular state changes
    const unsubscribe = $on('$stateChangeSuccess', handleRouteChange);

    // Also listen for popstate events (browser back/forward)
    window.addEventListener('popstate', handleRouteChange);
    // Listen for hash changes
    window.addEventListener('hashchange', handleRouteChange);

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
      window.removeEventListener('popstate', handleRouteChange);
      window.removeEventListener('hashchange', handleRouteChange);
    };
  }, [language, getLanguageFromUrl]);

  // Load data when language changes
  useEffect(() => {
    if (!language) {
      console.log('No language set, skipping data load');
      return;
    }

    console.log('Loading translations for language:', language);

    const loadDataAsync = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load source texts and translations in parallel
        const [sourceData, translationData] = await Promise.all([
          getAllSourceTexts(),
          getLanguageTranslations({ language }),
        ]);

        console.log(
          `Loaded ${sourceData.length} source texts and ${translationData.length} translations for ${language}`,
        );

        setSourceTexts(sourceData);
        setTranslations(translationData);

        // If there's a selected source text, find it again in the new data
        if (selectedSourceText) {
          const currentSourceText = sourceData.find(
            st =>
              st.namespace === selectedSourceText.namespace &&
              st.key === selectedSourceText.key,
          );

          if (currentSourceText) {
            // Update with the actual source text object from the new data
            setSelectedSourceText(currentSourceText);

            // Find translation for this source text
            const translation = translationData.find(
              t =>
                t.namespace === currentSourceText.namespace &&
                t.key === currentSourceText.key,
            );

            if (translation) {
              setTranslationValue(translation.value);
            } else {
              setTranslationValue(currentSourceText.value);
            }
          } else {
            // Source text not found in new data, reset selection
            setSelectedSourceText(null);
            setTranslationValue('');
          }
        }
      } catch (err) {
        setError('Failed to load data');
        console.error('Failed to load data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadDataAsync();
  }, [language]);

  const handleSourceTextClick = useCallback(
    sourceText => {
      setSelectedSourceText(sourceText);
      const translation = translations.find(
        t => t.namespace === sourceText.namespace && t.key === sourceText.key,
      );
      if (translation) {
        setTranslationValue(translation.value);
      } else {
        setTranslationValue(sourceText.value);
      }
    },
    [translations],
  );

  const handleUpdate = async () => {
    if (!selectedSourceText || !translationValue.trim()) {
      setError('Please enter a translation value');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      // Find existing translation (from DB or static file)
      let savedTranslation;

      // Check if translation exists in DB (has _id) or only in static files
      const dbTranslation = translations.find(
        t =>
          t.namespace === selectedSourceText.namespace &&
          t.key === selectedSourceText.key &&
          t._id, // Only DB translations have _id
      );

      if (dbTranslation) {
        // Update existing translation in DB
        // Only update if value actually changed
        if (dbTranslation.value === translationValue.trim()) {
          setSuccess('Translation unchanged');
          setTimeout(() => setSuccess(null), 2000);
          return;
        }
        savedTranslation = await updateTranslation(dbTranslation._id, {
          value: translationValue.trim(),
        });
      } else {
        // Try to create new translation in DB (overrides static file)
        // If it fails due to duplicate, try to find and update it
        try {
          savedTranslation = await createTranslation({
            namespace: selectedSourceText.namespace,
            key: selectedSourceText.key,
            language,
            value: translationValue.trim(),
          });
        } catch (createErr) {
          // If creation fails (possibly duplicate), reload translations and try update
          if (
            createErr.response?.status === 400 ||
            createErr.response?.status === 409
          ) {
            // Reload to get the actual DB translation
            const refreshedTranslations = await getLanguageTranslations({
              language,
            });
            setTranslations(refreshedTranslations);

            const foundTranslation = refreshedTranslations.find(
              t =>
                t.namespace === selectedSourceText.namespace &&
                t.key === selectedSourceText.key &&
                t._id,
            );

            if (foundTranslation) {
              // Update the existing one
              savedTranslation = await updateTranslation(foundTranslation._id, {
                value: translationValue.trim(),
              });
            } else {
              throw createErr; // Re-throw if we still can't find it
            }
          } else {
            throw createErr; // Re-throw other errors
          }
        }
      }

      // Reload translations to get the updated list with proper _id
      const translationData = await getLanguageTranslations({ language });
      setTranslations(translationData);

      // Update the translation value in the UI to reflect the saved value
      const updatedTranslation = translationData.find(
        t =>
          t.namespace === selectedSourceText.namespace &&
          t.key === selectedSourceText.key,
      );

      if (updatedTranslation) {
        // Ensure the translation value matches what's in the database
        setTranslationValue(updatedTranslation.value);
      } else if (savedTranslation && savedTranslation.value !== undefined) {
        setTranslationValue(savedTranslation.value);
      } else {
        // Fallback: use the value we just saved
        setTranslationValue(translationValue.trim());
      }

      setSuccess('Translation updated successfully!');
      setTimeout(() => setSuccess(null), 3000);

      // Reload i18n translations to reflect the change immediately in the UI
      // This ensures the saved translation appears in the interface right away
      const namespaces = i18n.options.ns || ['translation'];
      const targetNamespace = selectedSourceText?.namespace || 'translation';

      // Reload the specific namespace that was updated
      i18n.reloadResources(language, targetNamespace).catch(err => {
        console.warn(
          `Failed to reload namespace ${targetNamespace} after update:`,
          err,
        );
      });

      // If the namespace is not in the list, reload all
      if (!namespaces.includes(targetNamespace)) {
        if (Array.isArray(namespaces)) {
          Promise.all(
            namespaces.map(ns => i18n.reloadResources(language, ns)),
          ).catch(err => {
            console.warn('Failed to reload namespaces after update:', err);
          });
        }
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.message ||
        err.message ||
        'Failed to update translation. Please try again.';
      setError(errorMessage);
      console.error('Failed to update translation:', err);
      if (err.response?.data) {
        console.error('Error details:', err.response.data);
      }
      setTimeout(() => setError(null), 5000);
    }
  };

  const handleAddNewString = async e => {
    e.preventDefault();
    if (!newKey || !newValue) return;

    try {
      setError(null);
      setSuccess(null);

      await createTranslation({
        namespace: 'translation',
        key: newKey,
        language,
        value: newValue,
      });

      setNewKey('');
      setNewValue('');
      setSuccess('New string added successfully!');
      setTimeout(() => setSuccess(null), 3000);

      // Close modal
      const modal = document.getElementById('addNewStringModal');
      if (modal) {
        // Use Bootstrap's jQuery modal if available, otherwise just hide
        if (window.$ && window.$(modal).modal) {
          window.$(modal).modal('hide');
        } else {
          modal.style.display = 'none';
          modal.classList.remove('show');
          document.body.classList.remove('modal-open');
          const backdrop = document.querySelector('.modal-backdrop');
          if (backdrop) {
            backdrop.remove();
          }
        }
      }

      // Reload data
      const [sourceData, translationData] = await Promise.all([
        getAllSourceTexts(),
        getLanguageTranslations({ language }),
      ]);
      setSourceTexts(sourceData);
      setTranslations(translationData);
    } catch (err) {
      setError('Failed to add new string');
      console.error('Failed to add new string:', err);
    }
  };

  const handleRegenerateSourceTexts = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess('Regenerating source texts...');

      // Reload source texts
      const sourceData = await getAllSourceTexts();
      setSourceTexts(sourceData);

      // If we have a selected source text, find it again in the new data
      if (selectedSourceText) {
        const currentSourceText = sourceData.find(
          st =>
            st.namespace === selectedSourceText.namespace &&
            st.key === selectedSourceText.key,
        );

        if (currentSourceText) {
          setSelectedSourceText(currentSourceText);

          // Find translation for this source text
          const translation = translations.find(
            t =>
              t.namespace === currentSourceText.namespace &&
              t.key === currentSourceText.key,
          );

          if (translation) {
            setTranslationValue(translation.value);
          } else {
            setTranslationValue(currentSourceText.value);
          }
        } else {
          setSelectedSourceText(null);
          setTranslationValue('');
        }
      }

      setSuccess('Source texts regenerated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to regenerate source texts');
      console.error('Failed to regenerate source texts:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = useCallback(
    async newLanguage => {
      // Normalize language code to ensure consistency
      const normalizedLang = newLanguage?.trim() || 'en';

      // Only proceed if language actually changed
      if (normalizedLang === language) {
        console.log('Language is already', normalizedLang);
        return;
      }

      console.log('Changing language from', language, 'to', normalizedLang);

      // Change the i18n language first - this updates the UI immediately
      // This also saves to cookie automatically via LanguageDetector
      await i18n.changeLanguage(normalizedLang);

      // Explicitly save to cookie to ensure persistence across entire app navigation
      // This is critical so language persists when admin navigates away from translate page
      // The LanguageDetector should do this automatically, but we ensure it
      if (i18n.services?.languageDetector?.cacheUserLanguage) {
        i18n.services.languageDetector.cacheUserLanguage(normalizedLang);
      }

      // Manual cookie fallback to ensure language persists globally
      const cookieName = 'i18n';
      const cookieValue = normalizedLang;
      const maxAge = 60 * 60 * 24 * 365; // 1 year in seconds
      const cookieString = `${cookieName}=${cookieValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
      document.cookie = cookieString;
      console.log(
        '[AdminTranslateEdit language change] Saved language to cookie:',
        cookieString,
      );
      console.log(
        '[AdminTranslateEdit language change] Verified cookie:',
        document.cookie.match(/i18n=([^;]+)/),
      );

      // Verify cookie was actually set
      const verifyCookie = document.cookie.match(/i18n=([^;]+)/);
      if (!verifyCookie || verifyCookie[1] !== normalizedLang) {
        console.error(
          '[AdminTranslateEdit] WARNING: Cookie was not set correctly! Expected:',
          normalizedLang,
          'Got:',
          verifyCookie,
        );
      } else {
        console.log(
          '[AdminTranslateEdit] Cookie verified successfully:',
          verifyCookie[1],
        );
      }

      // Save to database if user is logged in
      try {
        const user = getUser();
        if (user && user._id) {
          console.log(
            '[AdminTranslateEdit] Saving language to database:',
            normalizedLang,
          );
          await usersApi.update({ locale: normalizedLang });
          console.log(
            '[AdminTranslateEdit] Language saved to database successfully',
          );
        } else {
          console.log(
            '[AdminTranslateEdit] User not logged in, skipping database save',
          );
        }
      } catch (err) {
        // Don't fail language change if database update fails, but log it
        console.error(
          '[AdminTranslateEdit] Failed to save language to database:',
          err,
        );
      }

      // Reload all namespaces to ensure all translations are loaded
      const namespaces = i18n.options.ns || ['translation'];
      if (Array.isArray(namespaces)) {
        await Promise.all(
          namespaces.map(ns =>
            i18n.reloadResources(normalizedLang, ns).catch(err => {
              console.warn(`Failed to reload namespace ${ns}:`, err);
            }),
          ),
        );
      } else {
        await i18n.reloadResources(normalizedLang, namespaces).catch(err => {
          console.warn(`Failed to reload namespace ${namespaces}:`, err);
        });
      }

      // Update the state immediately (before URL change)
      // This ensures the UI updates right away
      setSelectedSourceText(null);
      setTranslationValue('');
      setSearchQuery('');
      setLanguage(normalizedLang);

      // Update the URL using Angular's go function
      // This will trigger route change events
      try {
        go('admin-translate-show', { language: normalizedLang });
      } catch (err) {
        console.error('Failed to navigate:', err);
        // Fallback: update URL manually
        const newUrl = `/admin/translate/show/${encodeURIComponent(
          normalizedLang,
        )}`;
        window.history.pushState({ language: normalizedLang }, '', newUrl);
        // Trigger a hashchange event to notify listeners
        window.dispatchEvent(new HashChangeEvent('hashchange'));
      }

      // Scroll to top for better UX
      window.scrollTo(0, 0);
    },
    [language], // Include language in deps to ensure fresh comparison
  );

  const filteredSourceTexts = sourceTexts.filter(
    st =>
      st.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      st.value.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <AdminLayout>
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-12">
            <h2>Edit Translations</h2>
            <nav aria-label="breadcrumb">
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <a href="/admin">Home</a>
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  Edit Translations for{' '}
                  {currentLanguage ? currentLanguage.label : language}
                </li>
              </ol>
            </nav>
          </div>
        </div>

        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-12">
            <h3>
              Translations for{' '}
              {currentLanguage ? currentLanguage.label : language}
            </h3>
            <div style={{ marginBottom: '15px' }}>
              <button
                type="button"
                className="btn btn-default"
                onClick={() => {
                  go('admin-translate');
                }}
                style={{ marginRight: '10px' }}
              >
                <span>««</span> All Languages
              </button>

              {/* Popular languages quick selector */}
              <div
                className="btn-group"
                role="group"
                style={{ marginRight: '10px' }}
              >
                {locales
                  .filter(locale =>
                    [
                      'en',
                      'fr',
                      'de',
                      'es',
                      'it',
                      'pt',
                      'pt_BR',
                      'nl',
                    ].includes(locale.code),
                  )
                  .map((locale, index) => {
                    const isSelected =
                      locale.code === normalizedLanguage ||
                      locale.code.replace(/-/g, '_') === normalizedLanguage;
                    // Use index in key to ensure uniqueness even if codes are duplicated
                    return (
                      <button
                        key={`lang-btn-${locale.code}-${index}`}
                        type="button"
                        className={`btn ${
                          isSelected ? 'btn-primary' : 'btn-default'
                        }`}
                        onClick={() => handleLanguageChange(locale.code)}
                        style={{ marginRight: '5px' }}
                      >
                        {locale.label}
                      </button>
                    );
                  })}
              </div>

              <button
                type="button"
                className="btn btn-warning"
                onClick={handleRegenerateSourceTexts}
                style={{ marginRight: '10px' }}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Regenerate Source Texts'}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-toggle="modal"
                data-target="#addNewStringModal"
              >
                <span>+</span> Add New String
              </button>
            </div>
            <p className="text-info">
              Select any source text to translate it, then enter your translated
              text in textarea and hit update.
            </p>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="row">
          <div className="col-sm-5">
            <div className="panel panel-default">
              <div
                className="panel-heading"
                style={{ backgroundColor: '#333', color: '#fff' }}
              >
                <div className="row">
                  <div className="col-sm-6">
                    <strong>Source Text</strong>
                  </div>
                  <div className="col-sm-6 text-right">
                    <strong>Translation Status</strong>
                  </div>
                </div>
              </div>
              <div className="panel-body" style={{ padding: '0' }}>
                <div style={{ padding: '10px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Search Source Text..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    style={{ marginBottom: '10px' }}
                  />
                </div>
                <div
                  style={{
                    maxHeight: '500px',
                    overflowY: 'auto',
                    borderTop: '1px solid #ddd',
                  }}
                >
                  {loading ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      >
                        <span className="sr-only">Loading...</span>
                      </div>
                      <p>Loading translations...</p>
                    </div>
                  ) : filteredSourceTexts.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                      <p className="text-muted">No source texts found.</p>
                    </div>
                  ) : (
                    filteredSourceTexts.map(st => {
                      const translation = translations.find(
                        t => t.namespace === st.namespace && t.key === st.key,
                      );
                      const isSelected =
                        selectedSourceText &&
                        selectedSourceText.namespace === st.namespace &&
                        selectedSourceText.key === st.key;

                      // Create a unique key by combining namespace, key, and value hash
                      // This prevents duplicate key warnings
                      const uniqueKey = `${st.namespace}-${st.key}-${btoa(
                        encodeURIComponent(st.value),
                      ).slice(0, 10)}`;

                      return (
                        <div
                          key={uniqueKey}
                          onClick={() => handleSourceTextClick(st)}
                          style={{
                            padding: '10px',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#e3f2fd' : '#f9f9f9',
                            borderBottom: '1px solid #eee',
                            borderLeft: isSelected
                              ? '3px solid #2196F3'
                              : '3px solid transparent',
                            transition: 'background-color 0.2s ease',
                          }}
                          onMouseEnter={e => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#f0f0f0';
                            }
                          }}
                          onMouseLeave={e => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#f9f9f9';
                            }
                          }}
                        >
                          <div className="row">
                            <div className="col-sm-6">
                              <div style={{ fontWeight: '500' }}>{st.key}</div>
                              <div
                                style={{
                                  fontSize: '12px',
                                  color: '#666',
                                  marginTop: '5px',
                                  fontStyle: 'italic',
                                }}
                              >
                                {st.value.length > 50
                                  ? `${st.value.substring(0, 50)}...`
                                  : st.value}
                              </div>
                            </div>
                            <div className="col-sm-6 text-right">
                              {translation ? (
                                <span
                                  className="label label-success"
                                  style={{
                                    fontSize: '11px',
                                    padding: '3px 8px',
                                    borderRadius: '3px',
                                  }}
                                >
                                  ✓ Translated
                                </span>
                              ) : (
                                <span
                                  className="label label-default"
                                  style={{
                                    fontSize: '11px',
                                    padding: '3px 8px',
                                    borderRadius: '3px',
                                  }}
                                >
                                  Needs translation
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-sm-7">
            {selectedSourceText ? (
              <div className="panel panel-default">
                <div
                  className="panel-heading"
                  style={{ backgroundColor: '#f5f5f5' }}
                >
                  <h4 className="panel-title" style={{ margin: 0 }}>
                    Edit Translation
                  </h4>
                </div>
                <div className="panel-body">
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Key:</strong>{' '}
                    <code>
                      {selectedSourceText.namespace}:{selectedSourceText.key}
                    </code>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>Original Text (English):</strong>
                    <div
                      style={{
                        backgroundColor: '#f8f9fa',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '5px',
                        border: '1px solid #e9ecef',
                      }}
                    >
                      {selectedSourceText.value}
                    </div>
                  </div>
                  <div style={{ marginBottom: '15px' }}>
                    <strong>
                      Translate to{' '}
                      {currentLanguage ? currentLanguage.label : language}
                    </strong>
                    <textarea
                      className="form-control"
                      rows="8"
                      value={translationValue}
                      onChange={e => setTranslationValue(e.target.value)}
                      style={{
                        marginTop: '5px',
                        resize: 'vertical',
                        fontFamily: 'inherit',
                      }}
                      placeholder="Enter translation here..."
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleUpdate}
                    >
                      Update Translation
                    </button>
                    <button
                      type="button"
                      className="btn btn-default"
                      onClick={() => {
                        setSelectedSourceText(null);
                        setTranslationValue('');
                      }}
                    >
                      Clear Selection
                    </button>
                    <button
                      type="button"
                      className="btn btn-info"
                      onClick={() => {
                        // Copy original text to translation field
                        setTranslationValue(selectedSourceText.value);
                      }}
                    >
                      Copy Original
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="panel panel-default">
                <div
                  className="panel-heading"
                  style={{ backgroundColor: '#f5f5f5' }}
                >
                  <h4 className="panel-title" style={{ margin: 0 }}>
                    Translation Editor
                  </h4>
                </div>
                <div
                  className="panel-body"
                  style={{ textAlign: 'center', padding: '40px 20px' }}
                >
                  <div
                    style={{
                      fontSize: '48px',
                      color: '#ddd',
                      marginBottom: '20px',
                    }}
                  >
                    <i className="glyphicon glyphicon-globe"></i>
                  </div>
                  <h4 style={{ color: '#777', marginBottom: '10px' }}>
                    Select a source text to translate
                  </h4>
                  <p className="text-muted">
                    Click on any item from the left panel to start translating
                    it to {currentLanguage ? currentLanguage.label : language}.
                  </p>
                  {translations.length > 0 && (
                    <div
                      className="alert alert-info"
                      style={{ marginTop: '20px' }}
                    >
                      <p>
                        <strong>Translation Progress:</strong>{' '}
                        {translations.length} of {sourceTexts.length} strings
                        translated (
                        {Math.round(
                          (translations.length / sourceTexts.length) * 100,
                        )}
                        %)
                      </p>
                      <div className="progress">
                        <div
                          className="progress-bar progress-bar-success"
                          style={{
                            width: `${
                              (translations.length / sourceTexts.length) * 100
                            }%`,
                          }}
                        >
                          {Math.round(
                            (translations.length / sourceTexts.length) * 100,
                          )}
                          %
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Add New String Modal */}
        <div
          className="modal fade"
          id="addNewStringModal"
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
                <h4 className="modal-title">Add New String</h4>
              </div>
              <form onSubmit={handleAddNewString}>
                <div className="modal-body">
                  <div className="form-group">
                    <label htmlFor="new-key">Key</label>
                    <input
                      type="text"
                      className="form-control"
                      id="new-key"
                      value={newKey}
                      onChange={e => setNewKey(e.target.value)}
                      placeholder="e.g. homepage.welcome"
                      required
                    />
                    <small className="form-text text-muted">
                      Use dot notation for namespacing (e.g.,{' '}
                      &quot;homepage.welcome&quot;)
                    </small>
                  </div>
                  <div className="form-group">
                    <label htmlFor="new-value">Value</label>
                    <textarea
                      className="form-control"
                      id="new-value"
                      rows="3"
                      value={newValue}
                      onChange={e => setNewValue(e.target.value)}
                      placeholder="Translated text"
                      required
                    />
                    <small className="form-text text-muted">
                      Enter the translated text in{' '}
                      {currentLanguage ? currentLanguage.label : language}
                    </small>
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-default"
                    data-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Add New String
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Language Selection Modal */}
        <div
          className="modal fade"
          id="languageSelectionModal"
          tabIndex="-1"
          role="dialog"
        >
          <div className="modal-dialog" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <button
                  type="button"
                  className="close"
                  data-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">&times;</span>
                </button>
                <h4 className="modal-title">Select Language to Translate</h4>
              </div>
              <div className="modal-body">
                <div className="row">
                  {locales.map((locale, index) => {
                    const isSelected =
                      locale.code === normalizedLanguage ||
                      locale.code.replace(/-/g, '_') === normalizedLanguage;
                    // Use index in key to ensure uniqueness even if codes are duplicated
                    return (
                      <div
                        key={`locale-${locale.code}-${index}`}
                        className="col-sm-6"
                        style={{ marginBottom: '10px' }}
                      >
                        <button
                          type="button"
                          className={`btn btn-block ${
                            isSelected ? 'btn-primary' : 'btn-default'
                          }`}
                          onClick={() => {
                            handleLanguageChange(locale.code);
                            const modal = document.getElementById(
                              'languageSelectionModal',
                            );
                            if (window.$ && window.$(modal).modal) {
                              window.$(modal).modal('hide');
                            }
                          }}
                          style={{ textAlign: 'left' }}
                        >
                          <strong>{locale.label}</strong>
                          <br />
                          <small>{locale.code}</small>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default"
                  data-dismiss="modal"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

AdminTranslateEdit.propTypes = {};
